import { useCallback, useEffect, useState } from "react";

export type AppTheme = "light" | "dark";

const storageKey = "paper-kingdom.theme";
const themeEvent = "paper-kingdom-theme-change";

function systemTheme(): AppTheme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function currentTheme(): AppTheme {
  const documentTheme = document.documentElement.dataset.theme;
  return documentTheme === "dark" || documentTheme === "light"
    ? documentTheme
    : systemTheme();
}

function applyTheme(theme: AppTheme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "dark" ? "#141816" : "#f5f3ed");
}

export function useTheme() {
  const [theme, setTheme] = useState<AppTheme>(currentTheme);

  useEffect(() => {
    const syncTheme = () => setTheme(currentTheme());
    window.addEventListener(themeEvent, syncTheme);
    window.addEventListener("storage", syncTheme);
    return () => {
      window.removeEventListener(themeEvent, syncTheme);
      window.removeEventListener("storage", syncTheme);
    };
  }, []);

  const selectTheme = useCallback((nextTheme: AppTheme) => {
    applyTheme(nextTheme);
    try {
      localStorage.setItem(storageKey, nextTheme);
    } catch {
      // The selected theme still applies for this page when storage is blocked.
    }
    setTheme(nextTheme);
    window.dispatchEvent(new CustomEvent(themeEvent));
  }, []);

  const toggleTheme = useCallback(() => {
    selectTheme(theme === "dark" ? "light" : "dark");
  }, [selectTheme, theme]);

  return { theme, selectTheme, toggleTheme };
}
